import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Users,
  UserPlus,
  UserCheck,
  Newspaper,
  FlaskConical,
  MessageSquare,
  Coffee,
  Radio,
  Sparkles,
  Layout,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface SuggestedHub {
  id: string;
  name: string;
  description: string | null;
  cover_image_url: string | null;
  icon: string | null;
  color: string | null;
  member_count: number;
  hub_type: string;
  is_official: boolean;
  topics: string[];
}

interface OnboardingHubSuggestionsProps {
  topicIds: string[];
  onComplete: (joinedHubIds: string[]) => void | Promise<void>;
  onSkip: () => void;
}

// Hub type to icon/color mapping
const HUB_TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  newsroom: {
    icon: <Newspaper size={16} />,
    color: 'bg-blue-500/15 text-blue-400',
    label: 'Newsroom',
  },
  research_lab: {
    icon: <FlaskConical size={16} />,
    color: 'bg-iris-500/15 text-iris-400',
    label: 'Research Lab',
  },
  forum: {
    icon: <MessageSquare size={16} />,
    color: 'bg-emerald-500/15 text-emerald-400',
    label: 'Forum',
  },
  salon: {
    icon: <Coffee size={16} />,
    color: 'bg-amber-500/15 text-amber-400',
    label: 'Salon',
  },
  broadcast: {
    icon: <Radio size={16} />,
    color: 'bg-red-500/15 text-red-400',
    label: 'Broadcast',
  },
};

const DEFAULT_HUB_TYPE = {
  icon: <Layout size={16} />,
  color: 'bg-iris-500/15 text-iris-400',
  label: 'Hub',
};

export default function OnboardingHubSuggestions({
  topicIds,
  onComplete,
  onSkip,
}: OnboardingHubSuggestionsProps) {
  const { t } = useTranslation();
  const [hubs, setHubs] = useState<SuggestedHub[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const getAuthToken = useCallback(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('authToken');
    return null;
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      const token = getAuthToken();
      try {
        const params = new URLSearchParams();
        topicIds.forEach((id) => params.append('topicIds', id));

        const res = await fetch(
          `${API_URL}/onboarding/suggestions/hubs?${params.toString()}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (res.ok) {
          const data = await res.json();
          setHubs(data.hubs || data || []);
        }
      } catch {
        // Non-critical, show empty state
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [topicIds, getAuthToken]);

  const toggleHub = (hubId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(hubId)) {
        next.delete(hubId);
      } else {
        next.add(hubId);
      }
      return next;
    });
  };

  const [saving, setSaving] = useState(false);

  const handleComplete = async () => {
    setSaving(true);
    await onComplete(Array.from(selectedIds));
    setSaving(false);
  };

  const getHubTypeConfig = (hubType: string) => {
    return HUB_TYPE_CONFIG[hubType] || DEFAULT_HUB_TYPE;
  };

  const formatMemberCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-emerald-500/15 flex items-center justify-center">
          <Users size={28} className="text-emerald-400" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          {t('onboarding.hubsTitle')}
        </h3>
        <p className="text-sm text-[#9a9aac]">
          {t('onboarding.hubsSubtitle')}
        </p>
      </div>

      {/* Hub Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="text-emerald-500 animate-spin" />
        </div>
      ) : hubs.length === 0 ? (
        <div className="text-center py-12">
          <Users size={40} className="mx-auto text-[#2a2a3a] mb-3" />
          <p className="text-[#9a9aac]">{t('onboarding.noHubSuggestions')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
          {hubs.map((hub) => {
            const isSelected = selectedIds.has(hub.id);
            const typeConfig = getHubTypeConfig(hub.hub_type);

            return (
              <div
                key={hub.id}
                className={`relative flex flex-col p-4 rounded-xl border transition-all ${
                  isSelected
                    ? 'border-emerald-500/40 bg-emerald-500/10'
                    : 'border-white/6 bg-white/2 hover:bg-white/4'
                }`}
              >
                {/* Cover Image or Gradient */}
                <div
                  className="absolute inset-0 rounded-xl overflow-hidden opacity-20"
                  style={{
                    background: hub.cover_image_url
                      ? `url(${hub.cover_image_url}) center/cover`
                      : hub.color
                      ? `linear-gradient(135deg, ${hub.color}40, ${hub.color}10)`
                      : 'linear-gradient(135deg, #6366f140, #6366f110)',
                  }}
                />

                <div className="relative z-10">
                  {/* Header Row */}
                  <div className="flex items-start gap-3 mb-2">
                    {/* Icon */}
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${typeConfig.color}`}
                    >
                      {hub.icon ? (
                        <span className="text-lg">{hub.icon}</span>
                      ) : (
                        typeConfig.icon
                      )}
                    </div>

                    {/* Name & Type */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-white truncate">
                          {hub.name}
                        </h4>
                        {hub.is_official && (
                          <Sparkles size={12} className="text-iris-400 shrink-0" />
                        )}
                      </div>
                      <span className="text-[12px] text-[#9a9aac] uppercase tracking-wide">
                        {typeConfig.label}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  {hub.description && (
                    <p className="text-sm text-[#8e8e9e] line-clamp-2 mb-3">
                      {hub.description}
                    </p>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-auto pt-2">
                    <span className="text-xs text-[#9a9aac] flex items-center gap-1">
                      <Users size={12} />
                      {t('onboarding.members', { count: formatMemberCount(hub.member_count) } as any) as string}
                    </span>

                    <button
                      onClick={() => toggleHub(hub.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        isSelected
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white/6 text-[#a0a0b0] hover:bg-white/10 hover:text-white border border-white/6'
                      }`}
                    >
                      {isSelected ? (
                        <>
                          <UserCheck size={14} />
                          {t('onboarding.joined')}
                        </>
                      ) : (
                        <>
                          <UserPlus size={14} />
                          {t('onboarding.join')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 mt-4 border-t border-white/6">
        <button
          onClick={onSkip}
          className="px-4 py-2 text-sm text-[#9a9aac] hover:text-white transition-colors"
        >
          {t('onboarding.skip')}
        </button>

        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <span className="text-xs text-[#9a9aac]">
              {t('onboarding.selected', { count: selectedIds.size })}
            </span>
          )}
          <button
            onClick={handleComplete}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-all disabled:opacity-50"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            {saving ? t('onboarding.saving', 'Saving...') : t('onboarding.startExploring')}
          </button>
        </div>
      </div>
    </div>
  );
}
